import { Link, useNavigate } from 'react-router-dom'
import { FcGoogle } from 'react-icons/fc'
import { trackEvent } from '@/lib/trackEvent'
import React, { useEffect, useState } from 'react'
import { useLoginUser } from '@/hooks/useLoginUser'
import LogoIcon from '@/components/shared/LogoIcon'
import { endpoints } from '@/services/api/endpoints'
import { updateUserInfo, clearUserInfo } from '@/store/features/userSlice'
import cookies from '@/utils/cookies'
import { useDispatch } from 'react-redux'
import { axios } from '@/services/api/axiosInstance'
import { AxiosError } from 'axios'
import { Alert, AlertDescription } from '@/components/atoms/alert'

const Login = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const accessToken = cookies.get('ai_tk')
  const [email, setEmail] = useState('')
  const { magicLinkSent, loginError, handleMagicLinkLogin, handleGoogleLogin } =
    useLoginUser()

  const fetchUser = () => {
    axios
      .get(endpoints.getUserData.url)
      .then((res) => {
        if (!res.data?.data?.user_info.is_onboarded) {
          window.open(
            'https://play.aicade.io/onboarding?redirectTo=https://beta.aicade.io',
            '_self'
          )
        } else {
          dispatch(
            updateUserInfo({
              name: res.data?.data?.user_info.name,
              email: res.data?.data?.user_info.email,
              picture: res.data?.data?.user_info.profile_image,
              userId: res.data?.data?.user_info.user_id,
              lastLogin: res.data?.data?.user_info?.['last-login'],
              role: res.data?.data?.user_info.access_type,
              isOnboarded: res.data?.data?.user_info.is_onboarded,
            })
          )
          navigate('/')
        }
      })
      .catch(() => {
        dispatch(clearUserInfo())
        cookies.remove('ai_tk')
        navigate('/login', { replace: true })
      })
  }

  useEffect(() => {
    if (accessToken) {
      fetchUser()
    }
  }, [accessToken])

  useEffect(() => {
    trackEvent('Login Page Viewed')
  }, [])

  const onGoogleLogin = () => {
    trackEvent('Google Login Attempted')
    handleGoogleLogin()
  }

  const onMagicLinkLogin = async (event: React.FormEvent) => {
    event.preventDefault()
    handleMagicLinkLogin(email)
  }

  const getErrorMessage = (error: AxiosError) => {
    if (error.response) {
      return (
        error.response.data.message || 'An error occurred. Please try again.'
      )
    } else if (error.request) {
      return 'No response received from server. Please check your internet connection.'
    } else {
      return error.message || 'An unexpected error occurred. Please try again.'
    }
  }

  const renderErrorMessage = () => {
    if (!loginError) return null

    const errorMessage = getErrorMessage(loginError)
    if (!errorMessage) return null // Don't render anything if we're showing the waitlist

    return (
      <div className="mt-4 w-full max-w-[360px] rounded-lg bg-red-100 p-4 text-sm leading-relaxed text-red-800">
        {errorMessage}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-lg">
      <div className="to-r-[#343434] relative w-full max-w-[420px] overflow-hidden rounded-[30px] border border-[#666666] bg-linear-to-tr from-black px-14 py-16">
        <div className="flex flex-col items-center space-y-4">
          <div className="flex flex-col items-center space-y-2">
            <LogoIcon />
            <h2 className="text-2xl font-bold text-white">Sign in to Aicade</h2>
            <p className="mt-1 text-[12px] text-gray-400">
              Let&apos;s make some magic happen with Aicade
            </p>
          </div>

          <div className="w-full">
            <button
              className="flex h-9 w-full items-center justify-center rounded-[36px] bg-white text-[12px] font-semibold text-black"
              onClick={onGoogleLogin}
            >
              <FcGoogle className="mr-3" />
              Sign in with Google
            </button>

            <div className="mx-auto flex max-w-[150px] items-center justify-center py-4">
              <div className="h-px w-full bg-gray-700" />
              <span className="px-3 text-[12px] font-medium text-gray-500">
                OR
              </span>
              <div className="h-px w-full bg-gray-700" />
            </div>

            <form className="space-y-4" onSubmit={onMagicLinkLogin}>
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                id="email"
                type="email"
                name="email"
                placeholder="Drop your email"
                className="h-9 w-full rounded-[36px] border border-white/50 bg-white/5 px-9 py-3 text-[12px] font-semibold text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
              <button
                className="focus-visible:ring-ring whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 flex flex-row items-center justify-center gap-4 bg-linear-to-r from-[#FF9346] to-[#FE0851] tracking-wide text-white transition-colors duration-200 px-4 py-2 font-brand h-9 w-full rounded-[36px] text-[12px] font-semibold"
                type="submit"
              >
                Sign In With Email
              </button>
            </form>
            <p className="mt-4 w-full text-center text-[12px] font-medium text-gray-400">
              By registering you agree to our{' '}
              <Link
                to="https://aicade.io/terms-of-use"
                className="text-white underline"
              >
                Terms and Conditions
              </Link>
            </p>
          </div>
          {renderErrorMessage()}
        </div>
        {magicLinkSent && (
          <Alert className="mt-7 w-full rounded-2xl border border-[#4d4d4d] bg-white/5 px-6 py-4 text-center text-[12px] font-medium text-green-500">
            <AlertDescription>
              A verification link has been sent to your email. Please click on
              it to complete the sign-in process.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  )
}

export default Login
